import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Skeleton, Stack, Typography } from '@mui/material';
import {
  selectRelatedTopicsByParams,
  selectSearchFilters,
} from '../../../reducers/search.selectors';
import { borderRadius, palette, spaces, typography } from '../../../assets/styles/themes/tokens';
import RelatedTopicsHint from './RelatedTopicsHint';

// The top row shows every topic the API returns and scrolls horizontally; only
// the inline card is capped, to keep it compact. The skeleton placeholder counts
// mirror these while loading (the top count is arbitrary since it isn't capped).
const TOP_SKELETON_COUNT = 6;
const INLINE_MAX_CHIPS = 5;

// Inline pills row is inserted after the 7th result card (index 6) in each tab.
// Anything fewer means the row sits at the very end and doesn't break up scroll.
export const RELATED_TOPICS_INLINE_INDEX = 6;

export type RelatedTopicsPillsVariant = 'top' | 'inline';

interface RelatedTopicsPillsProps {
  term?: string;
  onSelect: (topic: string) => void;
  variant?: RelatedTopicsPillsVariant;
}

const RelatedTopicsPills: React.FC<RelatedTopicsPillsProps> = ({
  term,
  onSelect,
  variant = 'top',
}) => {
  const { t } = useTranslation();
  const searchFilters = useSelector(selectSearchFilters);
  const params = term
    ? {
      term,
      subject: searchFilters?.subject,
      classYear: searchFilters?.classYear,
      schoolType: searchFilters?.schoolType,
    }
    : undefined;
  const topics = useSelector(selectRelatedTopicsByParams(params));

  if (!term) return null;

  // The loading state is read from the per-term cache, not the global fetch
  // status: that status is shared across every term and is often stale (e.g. left
  // at ERROR by an aborted fetch during navigation), which would wrongly suppress
  // the skeleton when arriving on the search page from elsewhere. The cache entry
  // is the reliable per-term signal:
  //   undefined -> not fetched yet / in flight  -> skeleton
  //   []        -> fetch resolved with no topics (or failed) -> render nothing
  //   [..]      -> topics available             -> chips
  const hasTopics = Array.isArray(topics) && topics.length > 0;
  const isLoading = topics === undefined;

  if (!isLoading && !hasTopics) return null;

  const isInline = variant === 'inline';
  const skeletonCount = isInline ? INLINE_MAX_CHIPS : TOP_SKELETON_COUNT;
  const titleValue = isInline
    ? t('Continue searching with similar topics:')
    : t('Related topics:');

  const chipsRow = (
    <Stack
      direction='row'
      alignItems='center'
      sx={isInline
        // Inline sits inside a card with no room to scroll, so wrap onto multiple
        // rows instead of letting chips overflow and clip at the card edge.
        ? {
          gap: spaces.xs,
          flexWrap: 'wrap',
        }
        // Top is a single row that scrolls horizontally on every viewport.
        : {
          gap: spaces.xs,
          flexWrap: 'nowrap',
          overflowX: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          paddingRight: spaces.md,
        }}
    >
      {!isInline && (() => {
        const titleNode = (
          <Typography
            sx={{
              ...typography.body2,
              fontWeight: 500,
              color: palette.text.secondary,
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}
          >
            {titleValue}
          </Typography>
        );
        // Mount the hint only when actual chips are visible — never on skeletons.
        return hasTopics ? <RelatedTopicsHint>{titleNode}</RelatedTopicsHint> : titleNode;
      })()}
      {isLoading
        ? Array.from({ length: skeletonCount }).map((_, idx) => (
          <Skeleton
            key={idx}
            variant='rounded'
            width={96}
            height={32}
            sx={{ borderRadius: borderRadius.full, flexShrink: 0 }}
          />
        ))
        : (isInline ? topics!.slice(0, INLINE_MAX_CHIPS) : topics!).map((topic) => (
          <Chip
            key={topic}
            label={topic}
            onClick={() => onSelect(topic)}
            variant='outlined'
            data-testid={`related-topic-${topic}`}
            sx={{
              flexShrink: 0,
              backgroundColor: 'transparent',
              borderColor: palette.divider,
              borderRadius: borderRadius.full,
              color: palette.text.primary,
              '& .MuiChip-label': {
                paddingTop: spaces.xxs,
                paddingBottom: spaces.xxs,
                paddingLeft: spaces.xs,
                paddingRight: spaces.xs,
                ...typography.chip.label,
              },
            }}
          />
        ))}
    </Stack>
  );

  if (isInline) {
    return (
      <Box
        data-testid='related-topics-pills-inline'
        sx={{
          width: '100%',
          backgroundColor: palette.background.paperElevation2,
          border: `1px solid ${palette.divider}`,
          borderRadius: borderRadius.xl,
          padding: spaces.md,
          display: 'flex',
          flexDirection: 'column',
          gap: spaces.xs,
        }}
      >
        <Typography
          sx={{
            ...typography.body2,
            fontWeight: 500,
            color: palette.text.secondary,
            paddingLeft: spaces.xs,
          }}
        >
          {titleValue}
        </Typography>
        {chipsRow}
      </Box>
    );
  }

  return (
    <Box
      data-testid='related-topics-pills'
      sx={{
        width: '100%',
        paddingTop: spaces.md,
        paddingLeft: spaces.md,
        paddingBottom: spaces.md,
      }}
    >
      {chipsRow}
    </Box>
  );
};

export default RelatedTopicsPills;
